
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const MilnertonPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Milnerton page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Milnerton Painters",
      "description": "Professional painting contractors in Milnerton, Western Cape. Expert interior, exterior & roof painting services for coastal properties and residential homes.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/milnerton-painters`,
      "telephone": "",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Milnerton",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8769",
        "longitude": "18.4841"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "-33.8769",
          "longitude": "18.4841"
        },
        "geoRadius": "5000"
      },
      "services": [
        "Interior Painting Milnerton",
        "Exterior Painting Milnerton", 
        "Roof Painting Milnerton",
        "Commercial Painting Milnerton",
        "Coastal Property Painting Milnerton"
      ],
      "areaServed": "Milnerton, Western Cape, South Africa"
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Milnerton Painters - Professional Coastal Painting Contractors Western Cape | Get Quote";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting contractors in Milnerton, Western Cape. Expert coastal property painting services. Specialized in salt-resistant coatings and marine environment protection. Get your free quote today.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Milnerton painters, coastal painting contractors, house painters Western Cape, marine painting, salt-resistant coatings');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Interior Painting',
      description: 'Transform your Milnerton home with professional interior painting that withstands coastal humidity.',
      link: '/interior-painting'
    },
    {
      icon: 'ri-building-line',
      title: 'Exterior Painting',
      description: 'Protect your Milnerton property with salt-resistant exterior painting solutions.',
      link: '/exterior-painting'
    },
    {
      icon: 'ri-home-2-line',
      title: 'Roof Painting',
      description: 'Specialized roof painting for Milnerton\'s coastal environment with marine-grade coatings.',
      link: '/roof-painting'
    },
    {
      icon: 'ri-office-building-line',
      title: 'Commercial Painting',
      description: 'Professional commercial painting services for Milnerton businesses and marine facilities.',
      link: '/commercial-painting'
    },
  ];

  const whyChooseUs = [
    {
      icon: 'ri-ship-line',
      title: 'Coastal Expertise',
      description: 'Our Milnerton contractors specialize in marine environment painting with salt-resistant solutions.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Weather Protection',
      description: 'Advanced coatings designed specifically for Milnerton\'s coastal weather conditions.',
    },
    {
      icon: 'ri-time-line',
      title: 'Quick Service',
      description: 'Fast response times for all Milnerton painting projects and emergency coastal repairs.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Value Pricing',
      description: 'Competitive pricing for high-quality coastal painting services in Milnerton.',
    },
  ];

  const coastalFeatures = [
    {
      icon: 'ri-water-percent-line',
      title: 'Salt-Resistant Coatings',
      description: 'Specialized paints designed to withstand Milnerton\'s salt air and marine environment.'
    },
    {
      icon: 'ri-sun-line',
      title: 'UV Protection',
      description: 'Advanced UV-resistant formulations perfect for Milnerton\'s sunny coastal location.'
    },
    {
      icon: 'ri-drop-line',
      title: 'Moisture Barriers',
      description: 'Professional moisture protection systems for Milnerton\'s humid coastal conditions.'
    },
    {
      icon: 'ri-wind-line',
      title: 'Wind Resistance',
      description: 'Durable finishes that withstand Milnerton\'s strong coastal winds and weather.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/b9284cd68543cde60e239e63879052ac.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="block">Milnerton</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Professional coastal painting contractors serving Milnerton and surrounding areas.
          </p>
          <p className="text-lg md:text-xl mb-12 max-w-4xl mx-auto opacity-90">
            Connect with Milnerton's leading coastal painting specialists. We deliver exceptional 
            marine-grade painting solutions for residential and commercial properties.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Milnerton Quote
            </Link>
            <a 
              href="#services" 
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-black transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              View Services
            </a>
          </div>
        </div>
      </section>

      {/* About Milnerton Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Premier Coastal Painting Services in Milnerton
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Milnerton, situated along the stunning Western Cape coastline with breathtaking views 
                of Table Mountain and Robben Island, presents unique challenges for property painting. 
                The constant exposure to salt air, ocean winds, and intense UV radiation requires 
                specialized painting solutions that our network of professional contractors delivers.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                From the prestigious Milnerton Golf Club area to the bustling Milnerton Central 
                business district, our Milnerton painting contractors understand the specific needs 
                of coastal properties. We provide comprehensive painting services using marine-grade 
                materials designed to withstand the harsh coastal environment.
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Milnerton's Coastal Environment and Specialized Painting Requirements
              </h3>
              <p className="text-lg text-gray-600">
                The proximity to the Atlantic Ocean means Milnerton properties face constant exposure 
                to salt spray, high humidity, and strong winds. Our contractors use advanced coating 
                systems specifically formulated for marine environments, ensuring your property 
                maintains its beauty and protection year-round.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Beautiful%20coastal%20homes%20in%20Milnerton%20South%20Africa%20with%20ocean%20views%20and%20Table%20Mountain%20backdrop%2C%20professional%20painting%20work%20visible%20on%20waterfront%20properties%2C%20marina%20and%20boats%20in%20background%2C%20bright%20coastal%20lighting&width=600&height=400&seq=milnerton-coastal-homes&orientation=landscape"
                alt="Milnerton coastal properties with professional painting services"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Specialized Coastal Painting Services in Milnerton
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our Milnerton painting contractors provide marine-grade painting solutions for all coastal properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-100 group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${service.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-[#e9f638] transition-colors duration-200">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Coastal Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Advanced Coastal Protection for Milnerton Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized coating systems designed specifically for Milnerton's marine environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coastalFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${feature.icon} text-2xl text-black`}></i>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milnerton Specific Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Milnerton Painting Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored painting services for every type of Milnerton property and environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Milnerton Waterfront Property Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Specialized painting services for Milnerton's prestigious waterfront properties. 
                Our contractors use the highest grade marine coatings to protect against salt 
                corrosion, UV damage, and constant moisture exposure from the Atlantic Ocean.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Marine-grade coatings
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Salt corrosion protection
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  UV-resistant formulations
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Milnerton Golf Estate Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Premium painting services for properties in and around the renowned Milnerton Golf Club. 
                We understand the high standards expected in this prestigious area and deliver 
                exceptional results that enhance property values and curb appeal.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Premium quality finishes
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Color consultation services
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Property value enhancement
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Milnerton Commercial Marine Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Specialized commercial painting for Milnerton's marine-related businesses, including 
                boat yards, marinas, and waterfront commercial properties. We use industrial-grade 
                coatings designed for the harshest marine environments.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Industrial marine coatings
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Minimal business disruption
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Long-term durability
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Milnerton Coastal Painting Specialists
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Marine environment expertise combined with professional excellence for all Milnerton projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${item.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milnerton Areas Covered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Milnerton Areas We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive coastal painting services throughout Milnerton and surrounding neighborhoods
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Milnerton Central</h5>
              <p className="text-gray-600">
                The heart of Milnerton with its mix of residential and commercial properties, 
                all requiring specialized coastal painting solutions to withstand the marine environment.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Milnerton Golf Estate</h5>
              <p className="text-gray-600">
                Prestigious residential area around the famous golf course, featuring luxury homes 
                that demand the highest quality painting services and premium finishes.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Milnerton Waterfront</h5>
              <p className="text-gray-600">
                Prime coastal properties with direct ocean exposure requiring the most advanced 
                marine-grade painting systems and salt-resistant protective coatings.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Milnerton Ridge</h5>
              <p className="text-gray-600">
                Elevated residential areas with stunning views, benefiting from specialized painting 
                techniques that enhance the natural beauty while providing coastal protection.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Milnerton Industrial</h5>
              <p className="text-gray-600">
                Commercial and industrial properties near the harbor requiring heavy-duty marine 
                coatings and specialized painting systems for maximum durability.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h6 className="text-lg font-semibold text-gray-900 mb-3">Greater Milnerton Area</h6>
              <p className="text-gray-600">
                Extended service coverage including nearby coastal developments and properties 
                that benefit from our specialized marine environment painting expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our complete range of professional painting services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link 
              to="/interior-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Interior Painting
              </h3>
            </Link>

            <Link 
              to="/exterior-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Exterior Painting
              </h3>
            </Link>

            <Link 
              to="/roof-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Roof Painting
              </h3>
            </Link>

            <Link 
              to="/commercial-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Commercial Painting
              </h3>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Milnerton Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied customers throughout Milnerton's coastal communities
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Milnerton Coastal Painting Quote
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to protect your Milnerton coastal property? Connect with marine painting specialists today
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default MilnertonPage;
